import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BusinessLocationField from '../components/BusinessLocationField';
import IndustryField from '../components/IndustryField';
import BusinessDescriptionField from '../components/BusinessDescriptionField';
import ExitTimelineField from '../components/ExitTimelineField';
import HasGmField from '../components/HasGmField';
import PricesField from '../components/PricesField';
import ProfitMeasureField from '../components/ProfitMeasureField';
import BusinessPerformanceField from '../components/BusinessPerformanceField';
import UnacceptableOptionsField from '../components/UnacceptableOptionsField';
import CommercialFreeholdField from '../components/CommercialFreeholdField';
import LeaseField from '../components/LeaseField';
import SignupField from '../components/SignupField';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import ConfirmDetails from '../components/ConfirmDetails';

export type QuestionType = {
  title: string;
  Component: React.FC;
  description?: string;
  customQuestioLabel?: string;
};

export default function useQuestions() {
  const { t } = useTranslation();

  const { currentUser } = useContext(CurrentUserContext);

  const firstStepQuestions: QuestionType[] = useMemo(
    () => [
      {
        title: t('businessLocationTitle'),
        Component: BusinessLocationField,
      },
      {
        title: t('industryFieldTitle'),
        Component: IndustryField,
      },
      {
        title: t('businessDescriptionTitle'),
        Component: BusinessDescriptionField,
      },
      {
        title: t('exitTimelineTitle'),
        Component: ExitTimelineField,
      },
      {
        title: t('hasGmTitle'),
        Component: HasGmField,
      },
    ],
    [t],
  );

  const secondStepQuestions: QuestionType[] = useMemo(
    () => [
      {
        title: t('pricesTitle'),
        Component: PricesField,
      },
      {
        title: t('profiteMeasureTitle'),
        Component: ProfitMeasureField,
      },
      {
        title: t('businessPerformanceTitle'),
        Component: BusinessPerformanceField,
      },
      {
        title: t('consideredAspectsOfDealTitle'),
        description: t('pickAsYouLike'),
        Component: UnacceptableOptionsField,
      },
      {
        title: t('isTheCommercialFreeholdForSaleTitle'),
        Component: CommercialFreeholdField,
      },
      {
        title: t('isThereLeaseTitle'),
        Component: LeaseField,
      },
    ],
    [t],
  );

  const thirdStepQuestions: QuestionType[] = useMemo(
    () => [
      {
        title: t(
          currentUser ? 'vendorQualificationFinalTitle' : 'signupFieldTitle',
        ),
        description: t('signupFieldDescription'),
        customQuestioLabel: t('lastThing'),
        Component: currentUser ? ConfirmDetails : SignupField,
      },
    ],
    [currentUser, t],
  );

  const allQuestions = useMemo(
    () => [
      ...firstStepQuestions,
      ...secondStepQuestions,
      ...thirdStepQuestions,
    ],
    [firstStepQuestions, secondStepQuestions, thirdStepQuestions],
  );

  return {
    firstStepQuestions,
    secondStepQuestions,
    thirdStepQuestions,
    allQuestions,
  };
}
